import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ClientsProvider } from './contexts/ClientsContext';
import { CategoriesProvider } from './contexts/CategoriesContext';
import { ProductsProvider } from './contexts/ProductsContext';
import { DocumentsProvider } from './contexts/DocumentsContext';
import { EmployeesProvider } from './contexts/EmployeesContext';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import DocumentCreation from './components/DocumentCreation';
import Clients from './components/Clients';
import Employees from './components/Employees';
import Products from './components/Products';
import Categories from './components/Categories';
import Archives from './components/Archives';
import Settings from './components/Settings';
import { useTranslation } from 'react-i18next';
import './App.css';

// Initializes and handles language changes
function LanguageHandler() {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    // Check localStorage for language preference
    const savedSettings = localStorage.getItem('bridgeit-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        if (parsed.invoicing && parsed.invoicing.language) {
          i18n.changeLanguage(parsed.invoicing.language);
          
          // Set document direction based on language
          if (parsed.invoicing.language === 'ar') {
            document.documentElement.dir = 'rtl';
            document.documentElement.lang = 'ar';
          } else {
            document.documentElement.dir = 'ltr';
            document.documentElement.lang = parsed.invoicing.language;
          }
        }
      } catch (error) {
        console.error('Error loading language settings:', error);
      }
    }
  }, [i18n]);

  return null; // This component doesn't render anything
}

// Main App Content (after authentication)
function AppContent() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <SettingsProvider>
      <ClientsProvider>
        <CategoriesProvider>
        <ProductsProvider>
          <DocumentsProvider>
            <EmployeesProvider>
              <LanguageHandler />
              <div className="app">
                <Sidebar 
                  collapsed={sidebarCollapsed} 
                  onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
                />
                <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/document-creation" element={<DocumentCreation />} />
                    <Route path="/clients" element={<Clients />} />
                    <Route path="/employees" element={<Employees />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/categories" element={<Categories />} />
                    <Route path="/archives" element={<Archives />} />
                    <Route path="/settings" element={<Settings />} />
                  </Routes>
                </main>
              </div>
            </EmployeesProvider>
          </DocumentsProvider>
        </ProductsProvider>
        </CategoriesProvider>
      </ClientsProvider>
    </SettingsProvider>
  );
}

// Root App Component
function App() {
  // Set setup as completed by default - no setup page needed
  useEffect(() => {
    localStorage.setItem('setup_completed', 'true');
  }, []);

  return (
    <AuthProvider>
      <Router 
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <LanguageHandler />
        <Routes>
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App; 